import { ApiTypes, getApiBase } from '../helpers';
import { resolveAuthToken } from './authTokenResolver';
import type { HttpRequestConfig, HttpServiceConfig } from './types';

export type ApiDefaultError = {
  message: string;
  code?: string;
};

class HttpService {
  private baseUrl: string;

  private configAuthToken: string;

  constructor(config: HttpServiceConfig) {
    this.configAuthToken = config.authToken || '';
    this.baseUrl
      = config.baseUrl
        || getApiBase(config.apiType ?? ApiTypes.EDITOR, config.target);
  }

  private async request<T>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<T> {
    try {
      const headers = new Headers(config?.headers || {});

      const authToken = await resolveAuthToken(this.configAuthToken);
      if (authToken !== '') {
        headers.append('Authorization', `Bearer ${authToken}`);
      }
      headers.append('Content-Type', 'application/json');

      const finalUrl = `${this.baseUrl}${url}`;
      const response = await fetch(finalUrl, {
        headers,
        cache: 'no-cache',
        credentials: 'include',
        ...config,
      });

      if (!response.ok) {
        const errorData = (await response
          .json()
          .catch(() => ({ message: '' }))) as ApiDefaultError;
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in again.');
        }
        throw new Error(errorData.message || 'Unknown error');
      }

      if (config?.responseType === 'blob') {
        return (await response.blob()) as unknown as T;
      } else if (config?.responseType === 'text') {
        return (await response.text()) as unknown as T;
      }

      if (response.status === 204) {
        return {} as T;
      }

      return (await response.json()) as T;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  private processUrl(url: string, config?: HttpRequestConfig): string {
    let finalUrl = url;

    // Handle path parameters
    if (config?.params?.path) {
      Object.entries(config.params.path).forEach(([key, value]) => {
        finalUrl = finalUrl.replace(`{${key}}`, value);
      });
    }

    // Handle query parameters
    if (config?.params?.query) {
      const searchParams = new URLSearchParams();
      const flattenParams = (obj: Record<string, unknown>): void => {
        Object.entries(obj).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            return;
          }

          if (
            typeof value === 'object'
            && value !== null

            && Array.isArray(value) === false
          ) {
            // For nested objects, flatten with the correct key names
            Object.entries(value as Record<string, unknown>).forEach(
              ([nestedKey, nestedValue]) => {
                if (
                  nestedValue !== undefined
                  && nestedValue !== null
                  && nestedValue !== ''
                ) {
                  if (key === 'pagination') {
                    searchParams.append(
                      `pagination${nestedKey.charAt(0).toUpperCase() + nestedKey.slice(1)}`,
                      String(nestedValue),
                    );
                  } else if (key === 'sort') {
                    searchParams.append(
                      `sortBy${nestedKey.charAt(0).toUpperCase() + nestedKey.slice(1)}`,
                      String(nestedValue),
                    );
                  } else {
                    searchParams.append(
                      `${key}${nestedKey.charAt(0).toUpperCase() + nestedKey.slice(1)}`,
                      String(nestedValue),
                    );
                  }
                }
              },
            );
          } else {
            searchParams.append(key, String(value));
          }
        });
      };

      flattenParams(config.params.query);
      const queryString = searchParams.toString();
      if (queryString) {
        finalUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
      }
    }

    return finalUrl;
  }

  async get<T>(url: string, config?: HttpRequestConfig): Promise<T> {
    const finalUrl = this.processUrl(url, config);
    return this.request<T>(finalUrl, { method: 'GET', ...config });
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<T> {
    const finalUrl = this.processUrl(url, config);
    return this.request<T>(finalUrl, {
      method: 'POST',
      body: JSON.stringify(data),
      ...config,
    });
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<T> {
    const finalUrl = this.processUrl(url, config);
    return this.request<T>(finalUrl, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...config,
    });
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ): Promise<T> {
    const finalUrl = this.processUrl(url, config);
    return this.request<T>(finalUrl, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...config,
    });
  }

  async delete<T>(url: string, config?: HttpRequestConfig): Promise<T> {
    const finalUrl = this.processUrl(url, config);
    return this.request<T>(finalUrl, { method: 'DELETE', ...config });
  }
}

// Export singleton instance of InternalHttpService
export function idaHttpService(config: HttpServiceConfig) {
  return new HttpService({
    ...config,
    apiType: ApiTypes.IDA,
  });
}

// Export factory function for creating HttpService instances
export function httpService(config: HttpServiceConfig) {
  return new HttpService(config);
}
